import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Image, Upload } from "antd";
import imageCompression from "browser-image-compression";
import NoImageIcon from "assets/svgs/no_image_icon";
import { validateInput } from "modules/utilities/Validation";
import FalconCloseButton from "modules/common/FalconCloseButton";
import { useProfileUpdate } from "./store";
import { useToast } from "./store/toasterStore";
import { useUserProfile } from "./store/store";
import secureLocalStorage from "react-secure-storage";

function ProfileImageSection({ profileData, onRemoveImage }) {
  const hasProfileImage = !!profileData?.profileImage;

  return (
    <Col xl={4}>
      <div className="d-flex flex-column rounded shadow">
        <div style={{ minHeight: "7rem" }} className="text-center p-2">
          {hasProfileImage ? (
            <Image src={profileData.profileImage} height={100} className="" />
          ) : (
            <div className="d-flex flex-column justify-content-between align-items-center">
              <span>No image</span>
              <div className="pt-3">
                <NoImageIcon />
              </div>
            </div>
          )}
        </div>
        <div
          onClick={hasProfileImage ? onRemoveImage : undefined}
          className={`bg-${hasProfileImage ? "danger" : "secondary"} p-1 text-white fw-bold text-center rounded-bottom cursor-pointer`}
        >
          Remove
        </div>
      </div>
    </Col>
  );
}

function EditProfile() {
  const { updateProfile, removeProfile } = useProfileUpdate();
  const { setToaster } = useToast();
  const { profileData, fetchProfileDetails } = useUserProfile();

  const [formErrors, setFormErrors] = useState({});
  const [show, setShow] = useState(false);
  const [isImageJPEG, setIsImageJPEG] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [profileImage, setProfileImage] = useState();
  const [isImageSelected, setIsImageSelected] = useState(false);

  const [formData, setFormData] = useState(() => ({
    first_name: profileData?.userName,
    userAlternateEmail: profileData?.userAlternateEmail,
    profileImage: profileData?.profileImage,
  }));

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetchProfileDetails();
  }, []);

  useEffect(() => {
    setFormData({
      first_name: profileData?.userName,
      userAlternateEmail: profileData?.userAlternateEmail,
      profileImage: profileData?.profileImage,
    });
  }, [profileData]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleRemoveProfileImage = () => {
    setShow(false);
    removeProfile(setToaster, fetchProfileDetails);
  };

  const handleSubmit = async () => {
    setShow(false);
    const compressedFile = profileImage;
    const first_name = formData.first_name.trim();
    const userAlternateEmail = formData.userAlternateEmail.toLowerCase().trim();

    const data = compressedFile
      ? { compressedFile, first_name, userAlternateEmail }
      : { first_name, userAlternateEmail };

    updateProfile(data, secureLocalStorage.getItem("id"), setToaster, fetchProfileDetails);
  };

  const handleFormaDataChange = (event) => {
    const error = validateInput(event);
    setFormErrors((prevState) => ({
      ...prevState,
      [event.target.name]: error,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [event.target.name]: event.target.value,
    }));
  };

  const BeforeUpload = (file) => {
    if (file.type !== "image/jpeg") {
      setToaster("Image", `only Jpeg image is allowed`, "warning");
      return true;
    }
    return false;
  };

  const handleImageChange = async (image) => {
    const { fileList } = image;
    const hasSelectedImage = fileList.length > 0;
    setIsImageSelected(hasSelectedImage);
    setIsRemoved(false);

    if (!hasSelectedImage) {
      setIsImageJPEG(false);
      setImageError(false);
      setProfileImage(undefined);
      return;
    }

    if (image.file.type === "image/jpeg") {
      setIsImageJPEG(true);
      setImageError(false);

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 960,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(image.file, options);
      setProfileImage(compressedFile);
    } else {
      setImageError(true);
      setIsImageJPEG(false);
    }
  };

  const handleImageRemove = () => {
    setIsImageSelected(false);
    setIsRemoved(true);
  };

  return (
    <>
      <div onClick={handleShow}>Edit Profile</div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>Edit Profile</Modal.Title>
          <FalconCloseButton onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <ProfileImageSection
                profileData={profileData}
                onRemoveImage={handleRemoveProfileImage}
              />
              <Col className="text-center">
                {imageError && (
                  <span className="fw-bold text-danger">
                    Please add only image/jpeg file
                  </span>
                )}
                <Upload
                  accept="image/jpeg"
                  beforeUpload={(file) => BeforeUpload(file)}
                  listType="picture"
                  onChange={handleImageChange}
                  onRemove={handleImageRemove}
                  multiple={false}
                  className="d-flex flex-column-reverse justify-content-between h-100 align-items-center"
                >
                  <Button disabled={isImageSelected && !isRemoved}>
                    Upload Profile Picture
                  </Button>
                </Upload>
              </Col>
            </Row>
            <Row>
              <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder="Enter name"
                  name="first_name"
                  defaultValue={profileData?.userName}
                  onChange={handleFormaDataChange}
                />
                {formErrors.first_name && (
                  <label className="text-danger fw-bold">
                    {formErrors.first_name}
                  </label>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                <Form.Label>Alternate Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="userAlternateEmail"
                  placeholder="Enter email address"
                  defaultValue={profileData?.userAlternateEmail}
                  onChange={handleFormaDataChange}
                />
                {formErrors.userAlternateEmail && (
                  <label className="text-danger fw-bold">
                    {formErrors.userAlternateEmail}
                  </label>
                )}
              </Form.Group>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              (isImageSelected ? !isImageJPEG : false) ||
              Object.values(formErrors).some((element) => element != null && element !== "")
            }
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default EditProfile;
